<script setup lang="ts">
/**
 *
 *  Props and emits section
 *
 *
 */

const props = defineProps<{
    menu: any[]
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const router = useRouter()
const mainMenu = computed(() => props.menu)
const expandedMenus = ref(new Set())
const currentStoreStore = useCurrentStoreStore()

/**
 *
 *  Life cicle events
 *
 *
 */

/**
 *
 *  Functions section
 *
 *
 */
const isMenuExpanded = (menuItem: any) => {
    return expandedMenus.value.has(menuItem.title)
}

const toggleSubmenu = (menuItem: any) => {
    if (expandedMenus.value.has(menuItem.title)) {
        expandedMenus.value.delete(menuItem.title)
    } else {
        expandedMenus.value.add(menuItem.title)
    }
}

const handleClick = (menuItem: any) => {
    if (menuItem?.children && menuItem?.children.length > 0) {
        toggleSubmenu(menuItem)
    }

    if (menuItem?.route) {
        router.push(menuItem.route)
    }
}

const isActive = (menuItem: any) => {
    return (
        router.currentRoute.value.path === menuItem.route ||
        menuItem.children?.some((child: any) => isActive(child))
    )
}

const isShowMenuItem = (menuItem: any) => {
    if (menuItem.environment === "store") {
        return !!currentStoreStore.store?.id
    }
    return true
}
</script>

<template>
    <template v-for="menu in mainMenu">
        <div
            v-if="isShowMenuItem(menu)"
            class="tw-flex tw-flex-col tw-items-center tw-w-full"
        >
            <q-icon
                :name="menu.icon"
                :class="`tw-cursor-pointer tw-w-full small-menu-parent tw-py-4 ${isActive(menu) ? 'menuItem-active tw-text-adminAccent' : 'tw-text-adminAccent-300 hover:tw-text-adminAccent'}`"
                size="24px"
                @click="handleClick(menu)"
            />
            <div
                class="tw-relative tw-flex tw-flex-col tw-w-full tw-bg-adminPrimary-400"
            >
                <div
                    class="small-menu-arrow tw-absolute -tw-top-[7px] lg:-tw-top-[10px] tw-z-30 tw-left-0 tw-right-0 tw-mx-auto tw-w-[14px] tw-h-[14px] lg:tw-w-[20px] lg:tw-h-[20px] tw-rounded-[3px] tw-bg-adminPrimary-500 tw-rotate-45"
                ></div>
                <template v-for="menuItem in menu.children">
                    <div v-if="isShowMenuItem(menuItem)" class="tw-relative">
                        <div
                            :class="`tw-flex tw-justify-center tw-py-4 tw-px-2 tw-w-full tw-cursor-pointer small-menu-item-container ${isActive(menuItem) ? 'menuItem-active' : ''}`"
                            @click="handleClick(menuItem)"
                        >
                            <q-icon
                                :name="menuItem.icon"
                                :class="`${isActive(menuItem) ? 'tw-text-adminAccent' : 'tw-text-adminAccent-300'} small-menu-item tw-z-30 ${menuItem.children && menuItem.children.length > 0 ? 'tw-pb-1' : ''}`"
                                size="24px"
                            />
                        </div>
                        <template
                            v-if="
                                menuItem.children &&
                                menuItem.children.length > 0
                            "
                        >
                            <div
                                v-show="isMenuExpanded(menuItem)"
                                :class="`${isMenuExpanded(menuItem) ? 'submenuItem-expanded' : ''} small-submenu-container tw-relative tw-flex tw-flex-col tw-w-full tw-bg-adminPrimary-300`"
                            >
                                <div
                                    class="small-submenu-arrow tw-absolute -tw-top-[7px] lg:-tw-top-[10px] tw-z-30 tw-left-0 tw-right-0 tw-mx-auto tw-w-[14px] tw-h-[14px] lg:tw-w-[20px] lg:tw-h-[20px] tw-rounded-[3px] tw-bg-adminAccent tw-rotate-45"
                                ></div>
                                <div
                                    class="small-submenu-content tw-flex tw-flex-col tw-w-full tw-bg-adminAccent-300"
                                >
                                    <template
                                        v-for="menuItemChild in menuItem.children"
                                    >
                                        <div
                                            v-if="isShowMenuItem(menuItemChild)"
                                            :class="`tw-flex tw-justify-center tw-py-4 tw-px-2 tw-w-full tw-cursor-pointer tw-relative small-menu-item-container ${isActive(menuItemChild) ? 'menuItem-active' : ''}`"
                                            @click="handleClick(menuItemChild)"
                                        >
                                            <q-icon
                                                :name="menuItemChild.icon"
                                                class="tw-text-customGray-600 small-menu-item"
                                                size="24px"
                                            />
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
            </div>
        </div>
    </template>
</template>
<style scoped>
.menuItem-active::before {
    content: "";
    @apply tw-absolute -tw-right-[7px] lg:-tw-right-[10px] tw-top-0 tw-bottom-0 tw-my-auto tw-w-[14px] tw-h-[14px] lg:tw-w-[20px] lg:tw-h-[20px] tw-rotate-45 tw-bg-adminAccent-100;
}

.small-menu-item-container:hover
    + .small-submenu-container
    .small-submenu-content,
.small-menu-item-container:hover
    + .small-submenu-container
    .small-submenu-arrow,
.small-menu-item-container:has(~ .submenuItem-expanded)
    + .small-submenu-container
    .small-submenu-arrow,
.small-submenu-container .small-submenu-content {
    @apply tw-block;
}

.small-menu-item-container:has(~ .submenuItem-expanded)
    + .small-submenu-container
    .small-submenu-arrow {
    @apply tw-bg-adminAccent-400;
}

.small-submenu-container:not(:hover) .small-submenu-arrow {
    @apply tw-hidden;
}

.small-menu-item-container:has(~ .submenuItem-expanded),
.small-menu-item-container:has(> .small-menu-item:hover) {
    @apply tw-bg-adminAccent-400;
}

.small-menu-item-container:hover .small-menu-item,
.small-menu-item-container:has(> .small-menu-item:hover) .small-menu-item {
    @apply tw-text-adminAccent;
}

.small-menu-item-container:has(+ .small-submenu-container:hover)
    + .small-submenu-container
    .small-submenu-arrow,
.small-menu-item-container:has(+ .small-submenu-container:hover) {
    @apply tw-bg-adminPrimary-300;
}

.small-menu-item-container:has(+ .small-submenu-container:hover)
    .small-menu-item {
    @apply tw-text-adminAccent;
}

.small-menu-parent:hover + div .small-menu-item {
    animation: pulse 1s 2;
}
.small-menu-item:hover,
div:hover > .small-menu-item {
    @apply tw-scale-150 tw-transition-all tw-duration-300;
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.3);
    }
    100% {
        transform: scale(1);
    }
}
</style>
